import { keepPreviousData } from '@tanstack/react-query';

import { trpc } from '@/api/tRpc';
import { useTenantReady } from '@/hooks/useTenantReady';
import { POLL_NORMAL, POLL_HISTORY } from '@/lib/query-options';

/**
 * Hook to fetch monitoring overview with auto-refresh.
 * Waits for environment context to be available before making the API call.
 *
 * @returns { object } Returns tRPC query result with system overview metrics.
 */
export const useMonitoringOverview = () => {
  const tenantReady = useTenantReady();

  return trpc.monitoring.overview.useQuery(undefined, {
    ...POLL_NORMAL,
    staleTime: 0,
    enabled: tenantReady,
  });
};

/** Valid time range values for monitoring queries */
export type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';

/**
 * Hook to fetch DLQ queue depth history from Prometheus.
 * Uses placeholderData to prevent skeleton flash when switching ranges.
 *
 * @param { TimeRange } range Time range string (1h, 6h, 24h, 7d, 30d).
 * @returns { object } Returns tRPC query result with DLQ history data.
 */
export const useDlqHistory = (range: TimeRange = '24h') => {
  const tenantReady = useTenantReady();

  return trpc.monitoring.dlqHistory.useQuery(
    { range },
    {
      ...POLL_HISTORY,
      enabled: tenantReady,
      placeholderData: keepPreviousData,
    },
  );
};

/**
 * Hook to fetch combined telemetry data from Prometheus.
 * Uses placeholderData to prevent skeleton flash when switching ranges.
 * Single request fetches throughput, latency, error rate, and in-flight data.
 *
 * @param { TimeRange } range Time range string (1h, 6h, 24h, 7d, 30d).
 * @returns { object } Returns tRPC query result with telemetry data.
 */
export const useTelemetry = (range: TimeRange = '24h') => {
  const tenantReady = useTenantReady();

  return trpc.monitoring.telemetry.useQuery(
    { range },
    {
      ...POLL_NORMAL,
      enabled: tenantReady,
      placeholderData: keepPreviousData,
    },
  );
};
